<script setup lang="ts">
import { ImageGrid, type ImageInfo } from '#shared'
import { computed, ref } from 'vue'
import type { Attachment, Message } from '#entities/message'
import type { FileAttachment, GifAttachment, ImageAttachment } from '../model/attachment'
import FileAttachmentView from './file-attachment-view.vue'
import FullscreenImageModal from './fullscreen-image-modal.vue'
import GifAttachmentView from './gif-attachment-view.vue'

const { message, variant = 'regular' } = defineProps<{ message: Message; variant?: 'regular' | 'compact' }>()

const getAttachmentsByType = (type: Attachment['type']) =>
  message.attachments.filter(attachment => attachment.type === type)

const gifAttachments = computed(() => getAttachmentsByType('gif') as GifAttachment[])
const imageAttachments = computed(() => getAttachmentsByType('image') as ImageAttachment[])
const fileAttachments = computed(() => getAttachmentsByType('file') as FileAttachment[])

const imageInfos = computed(() =>
  imageAttachments.value.map(attachment => ({
    src: import.meta.env.VITE_BASE_API_URL + attachment.url,
    alt: 'Attachement',
    width: attachment.width,
    height: attachment.height,
  }))
)

const maxImageArea = computed(() => {
  if (!imageInfos.value.length) return 0
  const baseImageArea = variant === 'compact' ? 80_000 : 160_000
  return Math.floor(baseImageArea * (Math.log(imageInfos.value.length) + 1))
})

const enlargedImageInfo = ref<ImageInfo | null>(null)
const onImageClick = (imageInfo: ImageInfo) => {
  enlargedImageInfo.value = imageInfo
}
const onFullscreenImageClose = () => {
  enlargedImageInfo.value = null
}
</script>

<template>
  <GifAttachmentView
    v-for="attachment in gifAttachments"
    :key="attachment.id"
    :attachment="attachment"
    :variant="variant"
  />

  <div :class="$style.imageGridWrap" v-if="imageInfos.length > 0">
    <ImageGrid :class="$style.imageGrid" :imageInfos="imageInfos" doFitNaturalSize :maxImageArea="maxImageArea">
      <template v-slot="{ imageInfo }">
        <button :class="$style.imageWrap" @click="onImageClick(imageInfo)">
          <img :class="$style.image" :src="imageInfo.src" :alt="imageInfo.alt" />
        </button>
      </template>
    </ImageGrid>
  </div>

  <FullscreenImageModal :isOpen="!!enlargedImageInfo" :imageInfo="enlargedImageInfo" @close="onFullscreenImageClose" />

  <ul v-if="fileAttachments.length > 0" :class="$style.fileAttachmentsContainer">
    <FileAttachmentView v-for="attachment in fileAttachments" :key="attachment.id" :attachment="attachment" />
  </ul>
</template>

<style lang="scss" module>
.imageGridWrap {
  max-width: 600px;
  max-height: calc(100vh - 160px);
}

.imageGrid {
  width: 100%;
  height: 100%;
}

.imageWrap {
  display: flex;
}

.image {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
  object-fit: cover;
}

.fileAttachmentsContainer {
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
